// Pure helpers for moving a client's EXCESS receipts against its OVERDUE invoices.
// No Firestore. Cash-neutral: it only re-labels money the client has already paid,
// so profit, GST and WHT are untouched — only the outstanding amounts move.
import { round } from "./whtCalculator";

export const EXCESS_TOLERANCE = 5; // same tolerance as the AR register

// Invoices the client has paid too much on (outstanding is negative).
export const excessSources = (rows) => rows
  .filter((row) => Number(row.outstanding) < -EXCESS_TOLERANCE)
  .map((row) => ({ id: row.id, invoiceNumber: row.invoiceNumber, invoiceDate: row.invoiceDate, available: round(-row.outstanding) }))
  .sort((a, b) => String(a.invoiceDate).localeCompare(String(b.invoiceDate)));

// Invoices that are past due and still owed, oldest due date first.
export const overdueTargets = (rows) => rows
  .filter((row) => Number(row.outstanding) > EXCESS_TOLERANCE && Number(row.daysPastDue) > 0)
  .map((row) => ({ id: row.id, invoiceNumber: row.invoiceNumber, dueDate: row.dueDate, daysPastDue: row.daysPastDue, outstanding: round(row.outstanding) }))
  .sort((a, b) => String(a.dueDate).localeCompare(String(b.dueDate)));

export const poolOf = (sources) => sources.reduce((sum, source) => sum + source.available, 0);

// Suggested split: fill the oldest overdue invoices first until the excess runs out.
export function autoFill(pool, targets) {
  let remaining = round(pool);
  const amounts = {};
  targets.forEach((target) => {
    const take = Math.min(remaining, target.outstanding);
    if (take > 0) { amounts[target.id] = take; remaining -= take; }
  });
  return amounts;
}

export function validateExcessAdjustment({ amounts, targets, pool, note, date }) {
  const entries = Object.entries(amounts).map(([id, value]) => [id, round(value)]).filter(([, value]) => value > 0);
  if (!entries.length) return "Enter an amount against at least one overdue invoice.";
  for (const [id, value] of entries) {
    const target = targets.find((item) => item.id === id);
    if (!target) return "One of the invoices is no longer overdue.";
    if (value > target.outstanding) return `${target.invoiceNumber}: the amount is more than the ${target.outstanding.toLocaleString("en-PK")} still owed.`;
  }
  const total = entries.reduce((sum, [, value]) => sum + value, 0);
  if (total > round(pool)) return `You are applying PKR ${total.toLocaleString("en-PK")} but only PKR ${round(pool).toLocaleString("en-PK")} of excess is available.`;
  if (!String(note || "").trim()) return "Enter a note explaining this adjustment.";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(date || ""))) return "Enter the adjustment date.";
  return "";
}

// Draws the amounts from the excess invoices (oldest first) and returns one
// transfer per (excess invoice -> overdue invoice) pair.
export function planTransfers({ sources, amounts }) {
  const pool = sources.map((source) => ({ ...source, left: source.available }));
  const pairs = [];
  Object.entries(amounts).forEach(([toId, value]) => {
    let need = round(value);
    for (const source of pool) {
      if (need <= 0) break;
      const take = Math.min(source.left, need);
      if (take > 0) {
        pairs.push({ fromSnapshotId: source.id, fromInvoiceNumber: source.invoiceNumber, toSnapshotId: toId, amount: take });
        source.left -= take;
        need -= take;
      }
    }
    if (need > 0) throw new Error("Not enough excess is available to cover this adjustment.");
  });
  return pairs;
}

// Net effect of the (non-reversed) transfers on one invoice: positive reduces what
// it owes (it received money), negative means excess moved out of it.
export function creditAdjustmentFor(snapshotId, adjustments) {
  return adjustments.filter((item) => !item.reversed).reduce((sum, item) => {
    if (item.toSnapshotId === snapshotId) return sum + Number(item.amount || 0);
    if (item.fromSnapshotId === snapshotId) return sum - Number(item.amount || 0);
    return sum;
  }, 0);
}
